import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { filter, findIndex, toLower, forEach, get, isEmpty } from "lodash"
import { takeLatest, select, put, call, getContext } from "redux-saga/effects"
import { Types as LoaderTypes } from "model/data"
import { Creators as notificationCreators } from "modules/notifications/model"
import { getUserPreference } from "helpers/userPreferences"
import { getPortfolioId } from "../portfolio-root/selectors/portfolios"
import { getRawComparisonItems, getSelectedSettings, getBaseUrl } from "./selectors"

function updatePreferences(state) {
  return {
    type: "compareSettings",
    value: getSelectedSettings(state),
    key: getPortfolioId(state),
  }
}

export const { Types, Creators } = actionCreator("ompnt/modules/comparison/", {
  trackExperimentTypeSelection: {
    props: ["experimentType"],
    track: true,
    trackType: "compare-comparison-type-selected",
    trackPayload: ({ experimentType }) => ({ type: experimentType }),
  },
  trackPanelTrigger: {
    props: ["panel"],
    track: true,
    trackType: "compare-accordion-triggered",
    trackPayload: ({ panel }) => ({ accordion: panel }),
  },
  trackSetPeriod: {
    props: ["period"],
    track: true,
    trackType: "compare-date-period-updated",
    trackPayload: ({ period }) => ({ period }),
  },
  loadSavedComparison: ["compareSettings"],
  saveMetricToStore: {
    props: ["metric", "comparisonType"],
    track: true,
    trackType: "compare-graph-data-selected",
    trackPayload: ({ metric }) => ({ selected: metric }),
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  saveCompositionGroupToStore: {
    props: ["group"],
    track: true,
    trackType: "compare-composition-group-selected",
    trackPayload: ({ group }) => ({ group }),
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  removeNonOverlappingItemsSuccess: {
    props: ["comparisonItems"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  removeNonOverlappingItems: ["items"],
  evaluateAs: {
    props: ["item"],
    track: true,
    trackType: "compare-comparison-evaluate-as",
    trackPayload: {},
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  setComparisonItems: {
    props: ["items"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  loadSnapshot: ["payload"],
  setSnapshot: ["snapshot"],
  setSelectedComparisonType: {
    props: ["selectedComparisonType"],
    track: true,
    trackType: "comparison-type-view-switch",
    trackPayload: ({ selectedComparisonType }) => ({ view: selectedComparisonType }),
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
})

const DEFAULT_STATE = {
  comparisonItems: [],
  savedMetrics: {},
  savedCompositionGroup: "",
  snapshot: null,
}

function handleEvaluateAs(state, { item: { id, duplicate, evaluateAs } }) {
  const itemIndex = findIndex(state.comparisonItems, elem => elem.id === id && elem.duplicate === duplicate)
  const comparisonItems = [...state.comparisonItems]
  comparisonItems[itemIndex].evaluateAs = evaluateAs === "long" ? "short" : "long"
  return { ...state, comparisonItems }
}

function handleRemoveNonOverlappingItemsSuccess(state, { comparisonItems }) {
  return { ...state, comparisonItems }
}

function handleSaveMetricToStore(state, { metric, comparisonType }) {
  const metrics = state.savedMetrics ? { ...state.savedMetrics } : {}

  metrics[comparisonType] = metric

  return { ...state, savedMetrics: metrics }
}

function handleSaveCompositionGroupToStore(state, { group }) {
  return { ...state, savedCompositionGroup: group }
}

function handleLoadSavedComparison(state, { compareSettings }) {
  let comparisonItems = []
  let selectedComparisonType = { id: "performance" }
  let savedMetrics = {}
  let savedCompositionGroup = "SECTOR"

  if (compareSettings) {
    if (compareSettings.selectedComparisonType) {
      selectedComparisonType = compareSettings.selectedComparisonType
    }

    if (compareSettings.savedMetrics) {
      savedMetrics = compareSettings.savedMetrics
    }

    if (compareSettings.comparisonItems) {
      // Converted 'SWAP' type to 'swap', need to handle saved preference
      forEach(compareSettings.comparisonItems, elem => {
        elem.type = toLower(elem.type)
      })
      comparisonItems = compareSettings.comparisonItems
    }

    if (compareSettings.savedCompositionGroup) {
      savedCompositionGroup = compareSettings.savedCompositionGroup
    }
  }

  return {
    ...state,
    selectedComparisonType,
    savedMetrics,
    comparisonItems,
    savedCompositionGroup,
  }
}

function handleSetComparisonItems(state, { items }) {
  return { ...state, comparisonItems: items.filter(({ initialItem }) => !initialItem) }
}

function handleSetSnapshot(state, { snapshot }) {
  return { ...state, snapshot }
}

function handleLoadSnapshot(state, { payload }) {
  return handleSetComparisonItems({ ...state, snapshot: null }, { items: get(payload, "items", []) })
}

function handleSetSelectedComparisonType(state, { selectedComparisonType }) {
  return { ...state, selectedComparisonType }
}

export default createReducer(DEFAULT_STATE, {
  [Types.LOAD_SAVED_COMPARISON]: handleLoadSavedComparison,
  [Types.SAVE_METRIC_TO_STORE]: handleSaveMetricToStore,
  [Types.SAVE_COMPOSITION_GROUP_TO_STORE]: handleSaveCompositionGroupToStore,
  [Types.REMOVE_NON_OVERLAPPING_ITEMS_SUCCESS]: handleRemoveNonOverlappingItemsSuccess,
  [Types.EVALUATE_AS]: handleEvaluateAs,
  [Types.SET_COMPARISON_ITEMS]: handleSetComparisonItems,
  [Types.LOAD_SNAPSHOT]: handleLoadSnapshot,
  [Types.SET_SNAPSHOT]: handleSetSnapshot,
  [Types.SET_SELECTED_COMPARISON_TYPE]: handleSetSelectedComparisonType,
})

function* handleLoadSuccess(action) {
  const { snapshot } = yield select(state => ({
    snapshot: get(state, "modules.comparison.snapshot"),
  }))
  if (action.resource === "comparisonPreferences") {
    const preferedCompareSettings = getUserPreference(action.data, "compareSettings", null)
    if (snapshot) {
      yield put(Creators.loadSnapshot(snapshot))
    } else {
      yield put(Creators.loadSavedComparison(preferedCompareSettings))
    }
  }
}

function* handleRemoveNonOverlappingItems({ items }) {
  const { comparisonItems } = yield select(state => ({
    comparisonItems: getRawComparisonItems(state),
  }))

  const newComparisonItems = filter(comparisonItems, item => findIndex(items, { id: item.id }) === -1)

  yield put(notificationCreators.add("error", null, `${items.map(item => item.label).join(", ")} cannot be loaded because there are no overlapping dates with the current compare selection.`))
  yield put(Creators.removeNonOverlappingItemsSuccess(newComparisonItems))
}

function* goToURL(baseUrl, selectedComparisonType) {
  const { id, type, path } = selectedComparisonType
  let url = `${baseUrl}/${id}`
  if (type === "contentset") {
    url = `${baseUrl}/${type}/${id}`
  }
  // Most items use their id as the path but sometimes(camelcased names) we need a different path
  // eg. id: historicalFactorSensitivity -> path: factor-sensitivity/historical
  if (path) {
    url = `${baseUrl}/${path}`
  }

  const { navigate } = yield getContext("router")
  yield call(navigate, url, { replace: true })
}

function* selectDisplayType({ selectedComparisonType }) {
  // Get baseUrl from state
  const { baseUrl } = yield select(state => ({
    baseUrl: getBaseUrl(state),
  }))
  yield goToURL(baseUrl, selectedComparisonType)
}

function* handleNavigateWithSavedSettings() {
  const { baseUrl, compareSettings } = yield select(state => ({
    baseUrl: getBaseUrl(state),
    compareSettings: getSelectedSettings(state),
  }))
  const { selectedComparisonType } = compareSettings
  if (!isEmpty(selectedComparisonType)) {
    yield goToURL(baseUrl, selectedComparisonType)
  }
}

export function* saga() {
  yield takeLatest(LoaderTypes.LOAD_SUCCESS, handleLoadSuccess)
  yield takeLatest(Types.REMOVE_NON_OVERLAPPING_ITEMS, handleRemoveNonOverlappingItems)
  yield takeLatest(Types.SET_SELECTED_COMPARISON_TYPE, selectDisplayType)
  yield takeLatest(Types.SET_COMPARISON_ITEMS, handleNavigateWithSavedSettings)
}
